import { useState } from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import Select from '@/components/ui/Select'
import { useFieldDefinitions } from '@/features/contacts/useContacts'
import { useCreateFieldDefinition } from '@/features/settings/useFieldsAdmin'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import {
  FIELD_TYPES,
  NEW_FIELD_TYPES,
  OPTION_DATA_TYPES,
  SYSTEM_CONTACT_FIELDS,
  inputTypeForDataType,
  isMapped,
  targetValue,
  toFieldKey,
} from '@/features/forms/formBuilder'
import type { FormField } from '@/api/forms'

const inputCls =
  'px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

const TYPE_LABELS: Record<string, string> = {
  text: 'Short text',
  email: 'Email',
  tel: 'Phone',
  number: 'Number',
  date: 'Date',
  textarea: 'Long text',
  select: 'Dropdown',
  radio: 'Single choice',
  multiselect: 'Multiple choice',
  checkbox: 'Checkbox',
}

/**
 * The fields of a form, in the order visitors see them. Each has a label, where its answer is saved
 * (a contact or deal field, or "just collect"), and whether it's required; unsaved fields pick their
 * input type and choices. A new reusable custom field can be created without leaving the builder.
 */
export default function FieldsEditor({
  fields,
  onChange,
  createDeal,
}: {
  fields: FormField[]
  onChange: (fields: FormField[]) => void
  createDeal: boolean
}) {
  const { data: contactDefsRaw } = useFieldDefinitions('contact')
  const { data: dealDefsRaw } = useFieldDefinitions('deal')
  // File fields can't be captured from a public form (they need an upload), so they aren't mappable.
  const contactDefs = (contactDefsRaw ?? []).filter((d) => !d.hidden && d.dataType !== 'file')
  const dealDefs = (dealDefsRaw ?? []).filter((d) => !d.hidden && d.dataType !== 'file')
  const createField = useCreateFieldDefinition()
  const [newField, setNewField] = useState<{ entity: 'contact' | 'deal'; label: string; dataType: string } | null>(null)

  const patch = (i: number, p: Partial<FormField>) => onChange(fields.map((x, j) => (j === i ? { ...x, ...p } : x)))
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= fields.length) return
    const next = [...fields]
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }

  /** For an unmapped ("collect") field the key derives from the label; a mapped field keeps its key. */
  function setLabel(i: number, label: string) {
    onChange(
      fields.map((x, j) => {
        if (j !== i) return x
        if (isMapped(x)) return { ...x, label }
        const others = fields.filter((_, k) => k !== i).map((y) => y.key)
        let key = toFieldKey(label)
        if (key && others.includes(key)) {
          let n = 2
          while (others.includes(`${key}_${n}`)) n++
          key = `${key}_${n}`
        }
        return { ...x, label, key }
      }),
    )
  }

  /** Apply a target choice: sets key/entity/custom and derives the input type + options. */
  function setTarget(i: number, value: string) {
    onChange(
      fields.map((x, j) => {
        if (j !== i) return x
        if (value === 'collect') return { ...x, entity: 'contact', custom: false, key: toFieldKey(x.label) }
        const [ent, kind, key] = value.split(':')
        if (kind === 'sys') {
          const sys = SYSTEM_CONTACT_FIELDS.find((s) => s.key === key)
          return { ...x, entity: 'contact', custom: false, key, type: sys?.type ?? 'text', options: undefined, label: x.label || sys?.label || '' }
        }
        const def = (ent === 'd' ? dealDefs : contactDefs).find((d) => d.fieldKey === key)
        return {
          ...x,
          entity: ent === 'd' ? 'deal' : 'contact',
          custom: true,
          key,
          type: def ? inputTypeForDataType(def.dataType) : 'text',
          options: def && OPTION_DATA_TYPES.includes(def.dataType) ? (def.options ?? []).map((o) => o.value) : undefined,
          label: x.label || def?.label || '',
        }
      }),
    )
  }

  function submitNewField() {
    if (!newField || !newField.label.trim()) return
    createField.mutate(
      { entityType: newField.entity, fieldKey: toFieldKey(newField.label), label: newField.label.trim(), dataType: newField.dataType },
      { onSuccess: () => setNewField(null) },
    )
  }

  return (
    <div className="space-y-3">
      <ol className="space-y-2">
        {fields.map((f, i) => {
          const mapped = isMapped(f)
          return (
            <li key={i} className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="flex items-start gap-2">
                <span className="w-6 h-6 mt-2 shrink-0 rounded-full bg-gray-100 text-gray-500 text-xs font-medium flex items-center justify-center" aria-hidden>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0 grid gap-2 md:grid-cols-[1fr_14rem]">
                  <input
                    className={inputCls}
                    placeholder="Question / label visitors see"
                    aria-label="Field label"
                    value={f.label}
                    onChange={(e) => setLabel(i, e.target.value)}
                  />
                  <Select size="md" value={targetValue(f)} onChange={(e) => setTarget(i, e.target.value)} aria-label="Saved to" title="Where this answer is saved">
                    <option value="collect">Just collect (not saved to a record)</option>
                    <optgroup label="Contact">
                      {SYSTEM_CONTACT_FIELDS.map((s) => (
                        <option key={s.key} value={`c:sys:${s.key}`}>{s.label}</option>
                      ))}
                    </optgroup>
                    {contactDefs.length > 0 && (
                      <optgroup label="Contact fields">
                        {contactDefs.map((d) => (
                          <option key={d.id} value={`c:cf:${d.fieldKey}`}>{d.label}</option>
                        ))}
                      </optgroup>
                    )}
                    {dealDefs.length > 0 && (
                      <optgroup label={createDeal ? 'Deal fields' : 'Deal fields (turn on “Create a deal”)'}>
                        {dealDefs.map((d) => (
                          <option key={d.id} value={`d:cf:${d.fieldKey}`} disabled={!createDeal}>{d.label}</option>
                        ))}
                      </optgroup>
                    )}
                  </Select>
                  {!mapped && (
                    <div className="md:col-span-2 grid gap-2 md:grid-cols-[14rem_1fr]">
                      <Select
                        size="md"
                        aria-label="Input type"
                        value={f.type}
                        onChange={(e) => patch(i, { type: e.target.value, options: OPTION_DATA_TYPES.includes(e.target.value) ? f.options ?? [] : undefined })}
                      >
                        {FIELD_TYPES.map((t) => (
                          <option key={t} value={t}>{TYPE_LABELS[t] ?? t}</option>
                        ))}
                      </Select>
                      {OPTION_DATA_TYPES.includes(f.type) && (
                        <input
                          className={inputCls}
                          placeholder="Choices, comma-separated — e.g. Buying, Selling, Renting"
                          value={(f.options ?? []).join(',')}
                          onChange={(e) => patch(i, { options: e.target.value.split(',') })}
                        />
                      )}
                    </div>
                  )}
                  <div className="md:col-span-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    {!f.hidden && (
                      <label className="flex items-center gap-1.5 text-sm text-gray-700">
                        <input type="checkbox" checked={!!f.required} onChange={(e) => patch(i, { required: e.target.checked })} />
                        Required
                      </label>
                    )}
                    <label className="flex items-center gap-1.5 text-sm text-gray-700" title="Visitors don't see it; every submission saves the value you set">
                      <input
                        type="checkbox"
                        checked={!!f.hidden}
                        onChange={(e) => patch(i, e.target.checked ? { hidden: true, required: false, value: f.value ?? '' } : { hidden: false, value: undefined })}
                      />
                      Hidden, always saves:
                    </label>
                    {f.hidden && (OPTION_DATA_TYPES.includes(f.type) && (f.options ?? []).length > 0 ? (
                      <Select size="sm" aria-label="Hidden value" value={f.value ?? ''} onChange={(e) => patch(i, { value: e.target.value })}>
                        <option value="">Choose…</option>
                        {(f.options ?? []).filter((o) => o.trim()).map((o) => <option key={o} value={o}>{o}</option>)}
                      </Select>
                    ) : (
                      <input className={`${inputCls} w-56`} aria-label="Hidden value" placeholder="e.g. Buying a property" value={f.value ?? ''} onChange={(e) => patch(i, { value: e.target.value })} />
                    ))}
                    <span>{mapped ? `Saved to the ${f.entity === 'deal' ? 'deal' : 'contact'} · ${TYPE_LABELS[f.type] ?? f.type}` : 'Kept with the submission only'}</span>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="p-1 text-gray-400 hover:text-brand-primary disabled:opacity-30">
                    <ArrowUp size={15} />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === fields.length - 1} title="Move down" className="p-1 text-gray-400 hover:text-brand-primary disabled:opacity-30">
                    <ArrowDown size={15} />
                  </button>
                  <button type="button" onClick={() => onChange(fields.filter((_, j) => j !== i))} title="Remove field" className="p-1 text-gray-400 hover:text-danger">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </li>
          )
        })}
      </ol>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => onChange([...fields, { key: '', label: '', type: 'text', required: false, entity: 'contact', custom: false }])}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-brand-primary/40 text-sm font-medium text-brand-primary hover:bg-brand-primary/5"
        >
          <Plus size={15} /> Add field
        </button>
        <button
          type="button"
          onClick={() => setNewField(newField ? null : { entity: 'contact', label: '', dataType: 'text' })}
          className="text-sm text-gray-500 hover:text-brand-primary"
          title="Create a reusable custom field in your CRM, then use it on this form"
        >
          + New custom field
        </button>
      </div>

      {newField && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 grid gap-2 sm:grid-cols-[8rem_1fr_10rem_auto] items-center">
          <Select size="md" value={newField.entity} onChange={(e) => setNewField({ ...newField, entity: e.target.value as 'contact' | 'deal' })} aria-label="Field belongs to">
            <option value="contact">Contact</option>
            <option value="deal">Deal</option>
          </Select>
          <input className={inputCls} placeholder="New field name" value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} />
          <Select size="md" value={newField.dataType} onChange={(e) => setNewField({ ...newField, dataType: e.target.value })} aria-label="Field type">
            {NEW_FIELD_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
          <div className="flex gap-2">
            <button type="button" onClick={submitNewField} disabled={createField.isPending || !newField.label.trim()} className="px-3 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60">
              {createField.isPending ? 'Creating…' : 'Create'}
            </button>
            <button type="button" onClick={() => setNewField(null)} className="px-2 text-sm text-gray-500">Cancel</button>
          </div>
          {createField.error != null && <p className="text-danger text-xs sm:col-span-4">{apiErrorMessage(createField.error)}</p>}
        </div>
      )}
    </div>
  )
}
